import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest } from '@/lib/api/client'
import { apiPaths } from '@/lib/api/paths'
import type {
  LoginResponse,
  PartnerProfileResponse,
  TokenPair,
  TotpEnrolmentResponse,
} from '@/lib/api/types'
import { readRefreshToken } from '@/features/auth/session'
import { refreshTransport } from '@/lib/env'

export const partnerProfileKey = ['partner', 'profile'] as const

export const fetchPartnerProfile = (signal?: AbortSignal): Promise<PartnerProfileResponse> =>
  apiRequest<PartnerProfileResponse>(apiPaths.partnerProfile, { signal })

export const usePartnerProfile = (enabled = true): UseQueryResult<PartnerProfileResponse> =>
  useQuery({
    queryKey: partnerProfileKey,
    queryFn: ({ signal }) => fetchPartnerProfile(signal),
    enabled,
  })

export type RegisterInput = {
  email: string
  partnerKey: string
  password: string
}

export const register = (input: RegisterInput): Promise<{ status?: string }> =>
  apiRequest<{ status?: string }>(apiPaths.register, {
    method: 'POST',
    json: input,
    authenticated: false,
  })

export const login = (email: string, password: string): Promise<LoginResponse> =>
  apiRequest<LoginResponse>(apiPaths.login, {
    method: 'POST',
    json: { email, password },
    authenticated: false,
  })

export const completeTotpLogin = (preAuthToken: string, code: string): Promise<LoginResponse> =>
  apiRequest<LoginResponse>(apiPaths.loginTotp, {
    method: 'POST',
    json: { preAuthToken, code },
    authenticated: false,
  })

export const completeRecoveryCodeLogin = (
  preAuthToken: string,
  recoveryCode: string,
): Promise<LoginResponse> =>
  apiRequest<LoginResponse>(apiPaths.loginRecoveryCode, {
    method: 'POST',
    json: { preAuthToken, recoveryCode },
    authenticated: false,
  })

const refreshBody = (): Record<string, string> | undefined => {
  if (refreshTransport === 'cookie') {
    return undefined
  }

  const refreshToken = readRefreshToken()

  return refreshToken === null ? undefined : { refreshToken }
}

export const refreshTokens = (): Promise<TokenPair> =>
  apiRequest<TokenPair>(apiPaths.refresh, {
    method: 'POST',
    json: refreshBody() ?? {},
    authenticated: false,
    csrf: true,
  })

export const revokeSession = (): Promise<void> =>
  apiRequest<void>(apiPaths.logout, {
    method: 'POST',
    json: refreshBody() ?? {},
    authenticated: false,
    csrf: true,
  })

export const enrolTwoFactor = (): Promise<TotpEnrolmentResponse> =>
  apiRequest<TotpEnrolmentResponse>(apiPaths.twoFactorEnrol, { method: 'POST' })

export const activateTwoFactor = (code: string): Promise<void> =>
  apiRequest<void>(apiPaths.twoFactorActivate, { method: 'POST', json: { code } })

export const disableTwoFactor = (code: string): Promise<void> =>
  apiRequest<void>(apiPaths.twoFactorDisable, { method: 'DELETE', json: { code } })
